import React, { createContext, useContext, useState, useEffect } from 'react';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import configuredConfig from '../config/cognito-config';
import { UserData } from '../types';
import { USERS_URL } from '../config/api-config';

import { 
  User, 
  SystemInfo, 
  AuthContextType, 
  UserRole,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  Permission
} from '../types/auth-types';

// Initialize the user pool first
const userPool = new CognitoUserPool({
  UserPoolId: configuredConfig.userPoolId,
  ClientId: configuredConfig.clientId,
});

// Create the context with a default value
const defaultContextValue: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  systemInfo: null,
  handleOAuthCallback: async () => false,
  signOut: async () => false,
  getAuthToken: async () => null,
  setSystemInfo: () => {},
  hasPermission: () => false,
  checkAuthStatus: async () => false,
  refreshSession: async () => false,
};
const AuthContext = createContext<AuthContextType>(defaultContextValue);

// Keep track of the current user
let currentUser: CognitoUser | null = null;

export const setCurrentUser = (user: CognitoUser) => {
  currentUser = user;
};

const mockUser = {
  email: 'test@example.com',
  sub: 'test-sub-123',
  cognitoUsername: 'test-cognito-username-123',
  userId: 'test-sub-123',
  firstName: 'MockFirstName',
  lastName: 'MockLastName',
  role: 'ADMIN' as UserRole,
  // role: 'USER' as UserRole,
  // Add other required properties
};

// Create and export the provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Load user from localStorage if available
  const loadPersistedUser = () => {
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      return mockUser;
    }
    
    try {
      const persistedUser = localStorage.getItem('userData');
      if (persistedUser) {
        return JSON.parse(persistedUser);
      }
    } catch (error) {
      console.error('Error loading persisted user data:', error);
    }
    return null;
  };

  // Load system info from localStorage if available
  const loadPersistedSystemInfo = () => {
    try {
      const persistedSystemInfo = localStorage.getItem('systemInfo');
      if (persistedSystemInfo) {
        return JSON.parse(persistedSystemInfo);
      }
    } catch (error) {
      console.error('Error loading persisted system info:', error);
    }
    return null;
  };

  const [user, setUser] = useState<User | null>(loadPersistedUser());
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [systemInfo, setSystemInfoState] = useState<SystemInfo | null>(loadPersistedSystemInfo());

  const isAdmin = user?.role === 'ADMIN';

  // Enhanced setSystemInfo function that also persists to localStorage
  const setSystemInfo = (info: SystemInfo | null) => {
    setSystemInfoState(info);
    
    if (info) {
      localStorage.setItem('systemInfo', JSON.stringify(info));
    } else {
      localStorage.removeItem('systemInfo');
    }
  };

  // Enhanced user setter that also persists to localStorage
  const setUserWithPersistence = (newUser: User | null) => {
    setUser(newUser);
    
    if (newUser) {
      localStorage.setItem('userData', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('userData');
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user || !user.role) return false;
    
    // Verify that the role is a valid UserRole
    const role = user.role as UserRole;
    if (!Object.keys(ROLE_PERMISSIONS).includes(role)) return false;
    
    // Now TypeScript knows that role is a valid key of ROLE_PERMISSIONS
    return ROLE_PERMISSIONS[role].includes(permission as Permission);
  };

  // Helper functions to extract complex conditions and logic
  const hasValidTokens = (): boolean => {
    const accessToken = localStorage.getItem('accessToken');
    const idToken = localStorage.getItem('idToken');
    return !!(accessToken && idToken);
  };

  const isTokenExpired = (idTokenPayload: any): boolean => {
    const currentTime = Math.floor(Date.now() / 1000);
    return idTokenPayload.exp < currentTime;
  };

  const decodeIdToken = (idToken: string): any => {
    return JSON.parse(atob(idToken.split('.')[1]));
  };

  const extractCognitoUserData = (idTokenPayload: any) => {
    return {
      email: idTokenPayload.email,
      sub: idTokenPayload.sub,
      cognitoUsername: idTokenPayload['cognito:username'],
      firstName: idTokenPayload.given_name || '',
      lastName: idTokenPayload.family_name || '',
      profileImage: idTokenPayload.picture || undefined,
      isAdmin: idTokenPayload['cognito:groups']?.includes('Admins') || false
    };
  };

  const setAuthTokenAndStorage = (accessToken: string) => {
    setAuthToken(accessToken);
    localStorage.setItem('accessToken', accessToken);
  };

  const createUserData = (idTokenPayload: any, role: UserRole, profileImage?: string): User => {
    return {
      email: idTokenPayload.email,
      sub: idTokenPayload.sub,
      cognitoUsername: idTokenPayload['cognito:username'],
      userId: idTokenPayload.sub,
      firstName: idTokenPayload.given_name || '',
      lastName: idTokenPayload.family_name || '',
      role: role,
      profileImage: profileImage || undefined
    };
  };

  const handleExistingUser = async (idTokenPayload: any, cognitoUserData: any): Promise<boolean> => {
    try {
      const userDDBData = await getUserApi(idTokenPayload.sub);
      const role: UserRole = userDDBData.isAdmin ? 'ADMIN' : 'USER';

      const profileImage = userDDBData.profileImage || cognitoUserData.profileImage || null;
      const userData = createUserData(idTokenPayload, role, profileImage || undefined);
      
      setUserWithPersistence(userData);
      return true;
    } catch (error) {
      return false; // User doesn't exist, will be created
    }
  };

  const createNewUser = async (cognitoUserData: any): Promise<boolean> => {
    try {
      const currentTimestamp = new Date().toISOString();
      await createUserApi({
        email: cognitoUserData.email,
        sub: cognitoUserData.sub,
        cognitoUsername: cognitoUserData.cognitoUsername,
        profileImage: cognitoUserData.profileImage,
        isAdmin: cognitoUserData.isAdmin,
        isActive: true,
        createdAt: currentTimestamp,
        updatedAt: currentTimestamp,
        lastLogin: currentTimestamp,
        ownedItems: {
          playbookIds: [],
          toolIds: [],
          agentIds: [],
          adapterIds: []
        },
        allowedItems: {
          playbookIds: [],
          toolIds: [],
          agentIds: [],
          adapterIds: []
        }
      });

      const userData = createUserData(
        { 
          email: cognitoUserData.email, 
          sub: cognitoUserData.sub, 
          'cognito:username': cognitoUserData.cognitoUsername,
          given_name: cognitoUserData.firstName,
          family_name: cognitoUserData.lastName
        }, 
        cognitoUserData.isAdmin ? 'ADMIN' : 'USER', 
        cognitoUserData.profileImage
      );
      
      setUserWithPersistence(userData);
      return true;
    } catch (error) {
      console.error('Error creating user in DynamoDB:', error);
      await signOut();
      return false;
    }
  };

  const processUserAuthentication = async (idToken: string, accessToken: string): Promise<boolean> => {
    try {
      const idTokenPayload = decodeIdToken(idToken);
      
      if (isTokenExpired(idTokenPayload)) {
        await signOut();
        return false;
      }

      const cognitoUserData = extractCognitoUserData(idTokenPayload);
      setAuthTokenAndStorage(accessToken);

      const existingUserSuccess = await handleExistingUser(idTokenPayload, cognitoUserData);
      if (existingUserSuccess) {
        return true;
      }

      return await createNewUser(cognitoUserData);
    } catch (error) {
      console.error('Error verifying token:', error);
      await signOut();
      return false;
    }
  };

  const checkAuthStatus = async () => {
    try {
      if (!hasValidTokens()) {
        return false;
      }

      const accessToken = localStorage.getItem('accessToken')!;
      const idToken = localStorage.getItem('idToken')!;

      return await processUserAuthentication(idToken, accessToken);
    } catch (error) {
      console.error('Error in checkAuthStatus:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSession = async (): Promise<boolean> => {
    try {
          const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      return false;
    }
      
      const tokenEndpoint = `${configuredConfig.cognitoDomain}/oauth2/token`;
      
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: configuredConfig.clientId,
          refresh_token: refreshToken
        }).toString()
      });
      
      if (!response.ok) {
        console.error('Failed to refresh tokens');
        return false;
      }
      
      const tokens = await response.json();
      
      // Store new tokens
      localStorage.setItem('accessToken', tokens.access_token);
      localStorage.setItem('idToken', tokens.id_token);
      setAuthToken(tokens.access_token);
      
      // Update user info from the new ID token
      const idTokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));
      
      const userDDBData = await getUserApi(idTokenPayload.sub);
      const role: UserRole = userDDBData.isAdmin ? 'ADMIN' : 'USER';

      let profileImage = null;
      if (idTokenPayload.picture) {
        profileImage = idTokenPayload.picture;
      } 
      else if (idTokenPayload['picture'] || idTokenPayload['google:picture']) {
        profileImage = idTokenPayload['picture'] || idTokenPayload['google:picture'];
      }
      
      const userData: User = {
        email: idTokenPayload.email,
        sub: idTokenPayload.sub,
        cognitoUsername: idTokenPayload['cognito:username'],
        userId: idTokenPayload.sub,
        firstName: idTokenPayload.given_name,
        lastName: idTokenPayload.family_name,
        role: role,
        profileImage: profileImage || null
      };
      
      setUserWithPersistence(userData);
      
      return true;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  };

  // Helper functions to extract complex conditions and logic
  const isUserDisabled = (errorData: string): boolean => {
    return errorData.toLowerCase().includes('disabled');
  };

  const isUserDisabledInToken = (idTokenPayload: any): boolean => {
    return idTokenPayload['cognito:user_status'] === 'DISABLED';
  };

  const hasValidTokensFromResponse = (accessToken: string | undefined, idToken: string | undefined): boolean => {
    return !!(accessToken && idToken);
  };

  const extractTokens = (tokens: any) => {
    return {
      accessToken: tokens.access_token || tokens.AccessToken || tokens.accessToken,
      idToken: tokens.id_token || tokens.IdToken || tokens.idToken,
      refreshToken: tokens.refresh_token || tokens.RefreshToken || tokens.refreshToken
    };
  };

  const extractProfileImage = (idTokenPayload: any): string | null => {
    if (idTokenPayload.picture) {
      return idTokenPayload.picture;
    }
    if (idTokenPayload['picture'] || idTokenPayload['google:picture']) {
      return idTokenPayload['picture'] || idTokenPayload['google:picture'];
    }
    return null;
  };

  const storeTokens = (accessToken: string, idToken: string, refreshToken?: string) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('idToken', idToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  };

  const createUserDataForApi = (idTokenPayload: any, profileImage: string | null) => {
    return {
      email: idTokenPayload.email,
      sub: idTokenPayload.sub,
      cognitoUsername: idTokenPayload['cognito:username'],
      isAdmin: false,
      isActive: true,
      lastLogin: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ownedItems: {
        playbookIds: [],
        toolIds: [],
        agentIds: [],
        adapterIds: []
      },
      allowedItems: {
        playbookIds: [],
        toolIds: [],
        agentIds: [],
        adapterIds: []
      },
      profileImage: profileImage || null,
    };
  };

  const createUserObject = (idTokenPayload: any, role: UserRole, profileImage: string | null): User => {
    return {
      email: idTokenPayload.email,
      sub: idTokenPayload.sub,
      cognitoUsername: idTokenPayload['cognito:username'],
      userId: idTokenPayload.sub,
      firstName: idTokenPayload.given_name,
      lastName: idTokenPayload.family_name,
      role: role,
      profileImage: profileImage || undefined
    };
  };

  const setupCognitoUser = (idTokenPayload: any) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    const userData = {
      Username: idTokenPayload.email,
      Pool: userPool
    };
    const newCognitoUser = new CognitoUser(userData);
    setCurrentUser(newCognitoUser);
  };

  const exchangeCodeForTokens = async (code: string) => {
    const redirectUri = `${configuredConfig.cloudfrontDomain}/oauth/callback`;
    const tokenEndpoint = `${configuredConfig.cognitoDomain}/oauth2/token`;
    
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: configuredConfig.clientId,
        code: code,
        redirect_uri: redirectUri,
      }).toString()
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('Token endpoint error:', errorData);
      
      if (isUserDisabled(errorData)) {
        throw new Error('User is disabled');
      }
      
      throw new Error(`Failed to exchange code for tokens: ${errorData}`);
    }

    return await response.json();
  };

  const processTokens = async (tokens: any) => {
    const { accessToken, idToken, refreshToken } = extractTokens(tokens);
    
    if (!hasValidTokensFromResponse(accessToken, idToken)) {
      throw new Error('Invalid token response - missing access_token or id_token');
    }

    storeTokens(accessToken!, idToken!, refreshToken);
    setAuthToken(accessToken!);

    const idTokenPayload = JSON.parse(atob(idToken!.split('.')[1]));
    
    if (isUserDisabledInToken(idTokenPayload)) {
      throw new Error('User is disabled');
    }

    return { idTokenPayload, accessToken: accessToken! };
  };

  const processUserCreation = async (idTokenPayload: any) => {
    const profileImage = extractProfileImage(idTokenPayload);
    const userData = createUserDataForApi(idTokenPayload, profileImage);
    
    await createUserApi(userData);

    const userDDBData = await getUserApi(idTokenPayload.sub);
    const role: UserRole = userDDBData.isAdmin ? 'ADMIN' : 'USER';

    const newUser = createUserObject(idTokenPayload, role, profileImage);
    setUserWithPersistence(newUser);
    
    setupCognitoUser(idTokenPayload);
  };

  const cleanupOnError = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('systemInfo');
    setAuthToken(null);
    setUserWithPersistence(null);
  };

  const handleOAuthCallback = async (code: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      const tokens = await exchangeCodeForTokens(code);
      const { idTokenPayload } = await processTokens(tokens);
      await processUserCreation(idTokenPayload);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return true;
    } catch (error: any) {
      console.error('Error in handleOAuthCallback:', error);
      cleanupOnError();
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<boolean> => {
    try {
      const cognitoUser = userPool.getCurrentUser();
      let cognitoSignOutSuccess = true;
      
      // Attempt to sign out from Cognito
      if (cognitoUser) {
        try {
          cognitoUser.signOut();
          console.log('Successfully signed out from Cognito');
        } catch (cognitoError) {
          console.warn('Error signing out from Cognito:', cognitoError);
          cognitoSignOutSuccess = false;
          // Continue with cleanup even if Cognito signout fails
        }
      }
      
      // Clear all localStorage and sessionStorage items
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (storageError) {
        console.warn('Error clearing storage:', storageError);
        // Try to remove specific items if clear() fails
        try {
          localStorage.removeItem('authToken');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('idToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('systemInfo');
        } catch (removeError) {
          console.error('Error removing specific storage items:', removeError);
        }
      }
      
      // Reset application state
      setUserWithPersistence(null);
      setSystemInfo(null);
      setAuthToken(null);
      currentUser = null;
      
      // Return success only if all operations succeeded
      return cognitoSignOutSuccess;
    } catch (error) {
      console.error('Unexpected error during sign out:', error);
      
      // Even if there's an error, attempt cleanup
      try {
        setUserWithPersistence(null);
        setSystemInfo(null);
        setAuthToken(null);
        currentUser = null;
        
        // Force clear storage as fallback
        localStorage.clear();
        sessionStorage.clear();
      } catch (cleanupError) {
        console.error('Error during cleanup after sign out failure:', cleanupError);
      }
      
      // Return false to indicate the sign out process had errors
      return false;
    }
  };

  const getAuthToken = async (): Promise<string | null> => {
    // ALWAYS CHECK LOCALSTORAGE FIRST (synchronous and reliable)
    const storedToken = localStorage.getItem('accessToken');
    
    if (storedToken) {
      // Update React state for next time, but don't wait for it
      setAuthToken(storedToken);
      return storedToken;
    }
    
    // Fallback to React state (might be null due to timing)
    if (authToken) {
      return authToken;
    }
  
    // Try to get token from Cognito session
    const cognitoUser = userPool.getCurrentUser();
    
    if (cognitoUser) {
      try {
        const session = await new Promise<any>((resolve, reject) => {
          cognitoUser.getSession((err: any, session: any) => {
            if (err) {
              reject(err);
            } else {
              resolve(session);
            }
          });
        });
  
        if (session.isValid()) {
          const token = session.getAccessToken().getJwtToken();
          setAuthToken(token);
          localStorage.setItem('accessToken', token);
          return token;
        }
      } catch (error) {
        console.error('Error getting session:', error);
      }
    }
  
    return null;
  };

  // Create API functions directly using the real getAuthToken function
  const createApiHeaders = async (): Promise<HeadersInit> => {
    const token = await getAuthToken();
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    
    return {
      'X-User-Id': userData?.["userId"],
      'X-User-Email': userData?.["email"],
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  const getUserApi = async (userId: string) => {
    const headers = await createApiHeaders();
    const response = await fetch(`${USERS_URL}/${userId}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user details');
    }

    return response.json();
  };

  const createUserApi = async (user: any) => {
    const headers = await createApiHeaders();
    const response = await fetch(USERS_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(user)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create user');
    }

    return response.json();
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const contextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAdmin,
    handleOAuthCallback,
    signOut,
    getAuthToken,
    systemInfo,
    setSystemInfo,
    hasPermission,
    checkAuthStatus,
    refreshSession
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Export the hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export the user pool if needed elsewhere
export { userPool };

// Export constants for use in components
export { PERMISSIONS, ROLE_PERMISSIONS };